import { useState, useEffect } from 'react';
import { Search, FileText, X, ZoomIn, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function COAPage() {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCOA, setSelectedCOA] = useState(null);
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCOAs();
  }, []);

  const fetchCOAs = async () => {
    try {
      const res = await fetch(`${API}/api/coa`);
      if (res.ok) {
        const data = await res.json();
        setCoas(data);
      }
    } catch (err) {
      console.error('Error fetching COAs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter COAs based on search term
  const filteredCOAs = coas.filter(coa => 
    coa.product?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coa.batch?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20 lg:pt-24" data-testid="coa-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-custom py-12">
          <div className="max-w-2xl">
            <h1
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="coa-title"
            >
              {t('coa.title')}
            </h1>
            <p className="text-slate-600">
              {t('coa.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border-b border-slate-200 sticky top-16 lg:top-20 z-30">
        <div className="container-custom py-4">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder={t('coa.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 bg-white border-slate-200 rounded-sm"
              data-testid="coa-search-input"
            />
          </div>
          <p className="text-sm text-slate-500 mt-2">
            {filteredCOAs.length} {filteredCOAs.length === 1 ? 'certificate' : 'certificates'} available
          </p>
        </div>
      </div>

      {/* COA Grid */}
      <div className="container-custom py-8">
        {filteredCOAs.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-sm">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500" data-testid="no-coa-results">
              {t('coa.noResults')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCOAs.map((coa) => (
              <div 
                key={coa.id}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => setSelectedCOA(coa)}
                data-testid={`coa-card-${coa.id}`}
              >
                {/* COA Image Preview */}
                <div className="relative aspect-[3/4] bg-slate-100 overflow-hidden">
                  <img 
                    src={coa.image} 
                    alt={`COA - ${coa.product}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                
                {/* Product Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {coa.product}
                  </h3>
                  <p className="text-sm text-slate-500 font-mono mb-1">
                    Batch: {coa.batch}
                  </p>
                  <p className="text-sm text-green-600 font-medium">
                    Purity: {coa.purity}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* COA Image Modal */}
      <Dialog open={!!selectedCOA} onOpenChange={() => setSelectedCOA(null)}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 bg-black/95 border-none overflow-hidden" data-testid="coa-modal">
          {selectedCOA && (
            <div className="relative w-full h-full flex flex-col max-h-[90vh]">
              {/* Header */}
              <div className="flex-shrink-0 flex items-center justify-between p-4 bg-black/80">
                <div>
                  <h3 className="text-white font-semibold text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {selectedCOA.product}
                  </h3>
                  <p className="text-slate-400 text-sm font-mono">
                    Batch: {selectedCOA.batch}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setSelectedCOA(null)}
                  data-testid="close-coa-modal"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
              
              {/* Image - Scrollable Container */}
              <div className="flex-1 overflow-y-auto p-4 min-h-0">
                <img 
                  src={selectedCOA.image} 
                  alt={`Certificate of Analysis - ${selectedCOA.product}`}
                  className="w-full h-auto rounded-sm"
                  data-testid="coa-full-image"
                />
              </div>
              
              {/* Footer */}
              <div className="flex-shrink-0 p-4 bg-black/80 text-center">
                <p className="text-slate-400 text-sm">
                  Certificate of Analysis - PEP-T.COM
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
